/**
 * CatNatInfoPage — pagina informativa obbligo CAT NAT.
 *
 * Target SEO: "assicurazione CAT NAT obbligo imprese 2026",
 *             "polizza catastrofi naturali obbligatoria"
 *
 * Riferimenti: L. 213/2023 art. 1 c. 101-111, DM 18/2025, DL Milleproroghe 2025,
 * DL Omnibus 2026.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  AlertTriangle, ArrowRight, Calendar, ChevronRight,
  FileText, Gavel, HelpCircle, MapPin, Phone, Shield, Waves,
  Zap, Mountain,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAQ = [
  { q: "Chi è obbligato a stipulare la polizza CAT NAT?", a: "Tutte le imprese iscritte al Registro delle Imprese ai sensi dell'art. 2188 c.c. con sede o attività in Italia e che dispongono di beni strumentali ex art. 2424 c.c. lett. B.II (terreni, fabbricati, impianti, macchinari). Esclusi solo gli imprenditori agricoli (coperti dal Fondo AgriCat ex L. 197/2022)." },
  { q: "Quali eventi sono coperti obbligatoriamente?", a: "Sisma, alluvione, frana, inondazione ed esondazione sui beni strumentali iscritti in bilancio. Altri eventi atmosferici (vento, grandine, neve) sono opzionali." },
  { q: "Qual è la scadenza vigente?", a: "Grandi imprese: scaduta 31/10/2025. Piccole imprese: scaduta 31/12/2025. Medie imprese: 31/03/2026. Micro imprese: in proroga al 31/12/2026 (in valutazione). Le imprese che non si sono ancora adeguate devono attivarsi il prima possibile." },
  { q: "Cosa succede se non ho la copertura?", a: "Non ci sono sanzioni amministrative dirette. Tuttavia l'art. 1 c. 104 L. 213/2023 subordina l'accesso a contributi pubblici, sovvenzioni e agevolazioni post-calamità alla stipula della polizza. Senza CAT NAT l'impresa è esclusa dalla platea dei beneficiari." },
  { q: "Quanto costa la polizza?", a: "Il premio dipende da: zona sismica/idrogeologica, codice ATECO, valore dei beni assicurati, franchigie e massimali scelti. Con il nostro quotatore raccogliamo i dati necessari per formulare un preventivo personalizzato gratuito entro 24 ore lavorative." },
  { q: "Quali sono i massimali previsti dalla legge?", a: "DM 18/2025 fissa scoperto massimo al 15% della somma assicurata. Per somme sotto €1 mln il massimale minimo è il 100% del valore. Oltre €30 mln si applicano soglie derogabili." },
  { q: "Ho già una polizza All Risks: è sufficiente?", a: "Dipende dalle garanzie incluse. Molte polizze All Risks escludono gli eventi catastrofali o li coprono con sublimiti non conformi ai minimi DM 18/2025. Valutiamo gratuitamente la tua polizza attuale per identificare eventuali gap." },
  { q: "Posso integrarla con la mia polizza esistente?", a: "Sì. Offriamo la polizza CAT NAT come estensione autonoma o come sezione all'interno di un pacchetto All Risks Property rinnovato." },
  { q: "Quali beni sono assicurabili?", a: "Fabbricati (civili e non civili), terreni, macchinari al chiuso e all'aperto, merci, impianti rinnovabili (fotovoltaico, eolico, idroelettrico, biomassa), macchinari e baraccamenti di cantiere. Esclusi veicoli, aeromobili, beni off-shore, fabbricati abusivi." },
  { q: "Serve una perizia tecnica?", a: "Per somme elevate o rischi particolari (es. aree a classe sismica 1) la compagnia può richiedere sopralluogo di un perito convenzionato. Organizziamo noi l'appuntamento gratuitamente." },
];

const EVENTI = [
  { icon: Zap, nome: "Terremoto", desc: "Scosse sismiche e repliche entro 72h dalla prima registrazione", color: "text-amber-600 bg-amber-50" },
  { icon: Waves, nome: "Alluvione / Inondazione / Esondazione", desc: "Fuoriuscita di corsi d'acqua per piogge eccezionali", color: "text-blue-600 bg-blue-50" },
  { icon: Mountain, nome: "Frana", desc: "Movimento improvviso di terreno con distacco di masse rocciose", color: "text-stone-600 bg-stone-50" },
];

export default function CatNatInfoPage() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/catnat/info`).then(r => setInfo(r.data)).catch(() => {});
    document.title = "Colucci Assicuratori — CAT NAT";
    const meta = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) { tag = document.createElement("meta"); tag.setAttribute("name", name); document.head.appendChild(tag); }
      tag.setAttribute("content", content);
    };
    meta("description", "Polizza CAT NAT (Catastrofi Naturali) obbligatoria per le imprese ex L. 213/2023. Terremoto, alluvione, frana. Quota subito: preventivo gratuito in 24h.");
    meta("keywords", "CAT NAT obbligo imprese, assicurazione catastrofi naturali, polizza terremoto impresa, L. 213/2023, DM 18/2025, scadenza CAT NAT 2026");
    const ld = document.getElementById("ld-catnat") || (() => { const s = document.createElement("script"); s.id = "ld-catnat"; s.type = "application/ld+json"; document.head.appendChild(s); return s; })();
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "Product",
      name: "Polizza CAT NAT Imprese",
      description: "Assicurazione obbligatoria contro eventi catastrofali naturali per imprese iscritte al Registro Imprese ex L. 213/2023",
      category: "Insurance / Business / Catastrophic Events",
      brand: { "@type": "Organization", name: "Reale Mutua Assicurazioni" },
      offers: { "@type": "Offer", availability: "https://schema.org/InStock", areaServed: "IT", priceCurrency: "EUR", seller: { "@type": "InsuranceAgency", name: "Colucci Assicuratori" } },
    });
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      <Header />

      {/* HERO WARNING */}
      <section className="bg-gradient-to-br from-amber-600 to-amber-700 text-white py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <nav className="text-xs text-white/70 mb-3">
            <Link to="/aziende" className="hover:text-white">Aziende</Link>
            <ChevronRight className="w-3 h-3 inline mx-1" />
            <span className="text-white font-semibold">CAT NAT</span>
          </nav>
          <div className="inline-flex items-center gap-2 bg-white/15 px-3 py-1 rounded-full text-xs uppercase tracking-widest font-bold mb-4">
            <AlertTriangle className="w-3.5 h-3.5" /> Obbligo di legge
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl leading-tight mb-4">
            Polizza CAT NAT <span className="block">obbligatoria per le imprese</span>
          </h1>
          <p className="text-lg text-white/90 max-w-2xl leading-relaxed">
            La <strong>Legge di Bilancio 2024</strong> (L. 213/2023 art. 1 c. 101-111) impone a tutte le imprese iscritte al Registro Imprese di stipulare una polizza contro sisma, alluvione, frana e inondazione. Scopri se sei in regola.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/aziende/catnat/quotatore" className="inline-flex items-center gap-2 bg-white text-amber-700 hover:bg-amber-50 transition-colors px-6 py-3 rounded-full font-bold text-sm">
              Quota subito <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="tel:+39081680568" className="inline-flex items-center gap-2 border border-white/40 hover:bg-white/10 transition-colors px-6 py-3 rounded-full font-semibold text-sm">
              <Phone className="w-4 h-4" /> Parla con un consulente
            </a>
          </div>
        </div>
      </section>

      {/* CHI È OBBLIGATO */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="font-heading text-3xl text-[#163321] mb-5">Chi è obbligato</h2>
        <div className="bg-white rounded-2xl border border-[#E5E2DB] p-6 prose prose-sm max-w-none text-[#0F1F14]">
          <p>{info?.chi_e_obbligato || "Caricamento…"}</p>
          <p className="font-semibold mt-4 mb-2">Sono esclusi:</p>
          <ul>
            {(info?.esclusi || []).map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        </div>
      </section>

      {/* EVENTI COPERTI */}
      <section className="bg-white border-y border-[#E5E2DB] py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-3xl text-[#163321] mb-6">Eventi coperti</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {EVENTI.map((e, i) => {
              const Icon = e.icon;
              return (
                <div key={i} className="border border-[#E5E2DB] rounded-2xl p-5 space-y-2">
                  <div className={`w-12 h-12 rounded-xl ${e.color} flex items-center justify-center`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-heading text-lg text-[#0F1F14]">{e.nome}</h3>
                  <p className="text-sm text-[#4A5E51]">{e.desc}</p>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-[#4A5E51]/70 mt-4">Definizioni complete nelle Condizioni di Assicurazione Reale Mutua "Realmente Vicini Impresa" Ed. 11/2025.</p>
        </div>
      </section>

      {/* SCADENZE */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="font-heading text-3xl text-[#163321] mb-6 flex items-center gap-2">
          <Calendar className="w-7 h-7" /> Scadenze vigenti
        </h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {(info?.scadenze_2026 || []).map((s, i) => (
            <div key={i} className={`rounded-2xl p-4 border-2 ${
              s.stato === "vigente" ? "bg-emerald-50 border-emerald-500" :
              s.stato === "superata" ? "bg-red-50 border-red-400" :
              "bg-amber-50 border-amber-400"
            }`}>
              <p className="text-[10px] uppercase tracking-widest font-bold text-[#4A5E51]">{s.categoria}</p>
              <p className="text-2xl font-bold text-[#163321]">{s.scadenza}</p>
              <p className={`text-xs font-semibold mt-1 uppercase ${
                s.stato === "vigente" ? "text-emerald-700" :
                s.stato === "superata" ? "text-red-700" :
                "text-amber-700"
              }`}>
                {s.stato === "vigente" && "✅ In corso"}
                {s.stato === "superata" && "⚠️ Scaduta — adeguati subito"}
                {s.stato === "in_proroga" && "🕓 In proroga"}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CONSEGUENZE MANCATA COPERTURA */}
      <section className="bg-red-50 border-y border-red-200 py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-3xl text-red-900 mb-3 flex items-center gap-2"><Gavel className="w-7 h-7" /> Cosa succede se non mi copro</h2>
          <p className="text-sm text-red-800 leading-relaxed max-w-3xl">
            Ai sensi dell'<strong>art. 1 c. 104 L. 213/2023</strong> la mancata stipula della polizza CAT NAT <strong>esclude l'impresa dai contributi, sovvenzioni e agevolazioni pubbliche post-calamità</strong> — inclusi Bonus ZES, Bandi regionali, Fondi PNRR, interventi straordinari di ripristino. Non ci sono sanzioni amministrative dirette, ma l'effetto pratico è severo: in caso di evento catastrofale l'impresa sopporta per intero i danni senza poter accedere agli aiuti di Stato.
          </p>
        </div>
      </section>

      {/* CTA INTERMEDIA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 text-center">
        <h2 className="font-heading text-3xl text-[#163321] mb-3">Pronto a metterti in regola?</h2>
        <p className="text-[#4A5E51] text-sm mb-5">Compila il nostro quotatore in 5 minuti. Un consulente ti ricontatta entro 24 ore lavorative con preventivo gratuito e non vincolante.</p>
        <Link to="/aziende/catnat/quotatore" className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white transition-colors px-7 py-3 rounded-full font-bold">
          <Shield className="w-4 h-4" /> Quota ora
        </Link>
      </section>

      {/* FAQ */}
      <section className="bg-white border-t border-[#E5E2DB] py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-3xl text-[#163321] mb-6 flex items-center gap-2"><HelpCircle className="w-7 h-7" /> Domande frequenti</h2>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <details key={i} className="bg-[#F5F4F0] border border-[#E5E2DB] rounded-xl p-4 group">
                <summary className="cursor-pointer font-semibold text-[#0F1F14] list-none flex justify-between items-center text-sm">
                  {f.q}
                  <span className="text-[#163321] ml-2 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-[#4A5E51] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* NORMATIVA */}
      <section className="bg-[#163321] text-white py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="text-[10px] uppercase tracking-widest font-bold text-white/60 mb-2 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Quadro normativo</p>
          <ul className="text-sm space-y-1.5 text-white/80">
            <li>• <strong>Legge 30/12/2023 n. 213</strong> art. 1 c. 101-111 (Legge di Bilancio 2024) — istituzione dell'obbligo</li>
            <li>• <strong>DM MIMIT-MEF 18/2025</strong> — parametri tecnici minimi di copertura (franchigie max 15%, massimale min 100%)</li>
            <li>• <strong>DL Milleproroghe 2025</strong> e <strong>DL Omnibus 2026</strong> — proroghe scadenze</li>
            <li>• Prodotto distribuito: <strong>Reale Mutua "Realmente Vicini Impresa"</strong> (DIP Aggiuntivo Ed. 01/2026)</li>
            <li>• Intermediario: <strong>Colucci Assicuratori</strong> — RUI IVASS sez. A n. A000464752</li>
          </ul>
        </div>
      </section>

      <Footer />
    </div>
  );
}
